import { useEffect, useCallback } from 'react';

/**
 * Hook to register service worker and manage caching
 */
export const useServiceWorker = () => {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/service-worker.js')
          .then((registration) => {
            console.log('[App] Service Worker registered:', registration.scope);
            
            // Check for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    console.log('[App] New content available, refresh to update');
                  }
                });
              }
            });
          })
          .catch((error) => {
            console.log('[App] Service Worker registration failed:', error);
          });
      });
    }
  }, []);

  // Function to pre-cache media URLs
  const preCacheMedia = useCallback((urls) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CACHE_MEDIA',
        urls: urls
      });
    }
  }, []);

  // Function to clear all caches
  const clearCache = useCallback(() => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_CACHE'
      });
    }
  }, []);

  return { preCacheMedia, clearCache };
};

/**
 * Hook to preload critical assets on mount
 */
export const usePreloadAssets = (assets) => {
  useEffect(() => {
    if (!assets || assets.length === 0) return;

    // Preload images
    const imageAssets = assets.filter(a => 
      a.type === 'image' || 
      a.src?.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i)
    );
    
    imageAssets.forEach(asset => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = asset.src;
      document.head.appendChild(link);
    });

    // Preload video posters
    const videoAssets = assets.filter(a => a.type === 'video' && a.poster);
    videoAssets.forEach(asset => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = asset.poster;
      document.head.appendChild(link);
    });

    return () => {
      // Cleanup preload links on unmount (optional)
    };
  }, [assets]);
};

/**
 * Hook to lazy load images using Intersection Observer
 */
export const useLazyLoad = (ref, options = {}) => {
  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;
            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      },
      {
        rootMargin: options.rootMargin || '100px',
        threshold: options.threshold || 0.01
      }
    );

    const images = ref.current.querySelectorAll('img[data-src]');
    images.forEach((img) => observer.observe(img));

    return () => observer.disconnect();
  }, [ref, options]);
};

export default useServiceWorker;
